import {defineStore} from "pinia";
import RegistrationV4Api from "@/services/api/RegistrationV4Api.ts";
import {ref, type Ref} from "vue";
import {useErrorStore} from "@/stores/error.ts";

export interface BdmDetails {
    name: string;
    email: string;
    phone: string | null;
    meeting_url: string | null;
}

export const useRegistrationV4Store = defineStore('registrationV4', () => {
    const apiService = new RegistrationV4Api();
    const errorStore = useErrorStore();

    const origin: Ref<string> = ref('');
    const isSubmitting: Ref<boolean> = ref(false);
    const isComplete: Ref<boolean> = ref(false);
    const emailError: Ref<string | null> = ref(null);
    const bdm: Ref<BdmDetails | null> = ref(null);

    const init = (originProp: string) => {
        origin.value = originProp;
    };

    const submitRegistration = async (formData: object): Promise<boolean> => {
        emailError.value = null;
        isSubmitting.value = true;

        // Capture UTM parameters from URL
        const urlParams = new URLSearchParams(window.location.search);
        const utmParams: Record<string, string> = {};

        // Capture all UTM parameters and other tracking params
        const trackingParams = [
            'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
            'gclid', 'fbclid', 'msclkid', 'ttclid', 'li_fat_id'
        ];

        trackingParams.forEach(param => {
            const value = urlParams.get(param);
            if (value) {
                utmParams[param] = value;
            }
        });

        try {
            const response = await apiService.register({
                ...formData,
                origin: origin.value,
                utm_params: utmParams,
            });

            bdm.value = response.data?.data?.bdm ?? null;

            isComplete.value = true;

            try {
                const lintrk = (window as Window & {
                    lintrk?: (event: 'track', payload: { conversion_id: number }) => void;
                }).lintrk;
                if (typeof lintrk === 'function') {
                    lintrk('track', { conversion_id: 26741417 });
                }
            } catch (trackingError) {
                console.warn('LinkedIn tracking failed', trackingError);
            }

            return true;
        } catch (e: any) {
            if (e.response?.status === 422) {
                const errors = e.response.data?.errors;

                if (errors?.email) {
                    // Surface the duplicate-email error directly on the field
                    emailError.value = errors.email[0];
                } else {
                    errorStore.report(e.response.data?.message ?? 'Please check your details and try again.');
                }
            } else {
                errorStore.report('Something went wrong. Please try again.');
                console.error(e);
            }

            return false;
        } finally {
            isSubmitting.value = false;
        }
    };

    return {
        origin,
        isSubmitting,
        isComplete,
        emailError,
        bdm,
        init,
        submitRegistration,
    };
});
